import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import { Plus, Search, Edit, Trash2, Shield, X, Save } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit Modal ke States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [roles, setRoles] = useState([]); // 👈 Roles ka state ready hai

  useEffect(() => {
    fetchEmployees();
    fetchRoles(); // 👈 Page load hote hi roles bhi fetch karenge
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await api.get("/employees", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  // 👇 Dynamic Roles Fetch Karne Ka Logic 👇
  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await api.get("/roles", {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend agar 'data' key mein bhej raha hai toh usko handle kiya
      setRoles(response.data.data || response.data); 
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load roles for dropdown");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    
    try {
      const token = localStorage.getItem("admin_token");
      await api.delete(`/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Employee deleted successfully!");
      setEmployees(employees.filter(emp => emp.id !== id)); 
    } catch (error) {
      toast.error("Failed to delete employee.");
    }
  };

  const openEditModal = (emp) => {
    setEditData(emp);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      const token = localStorage.getItem("admin_token");
      const response = await api.put(`/employees/${editData.id}`, {
        name: editData.name,
        role: editData.role,
        status: editData.status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Employee updated successfully!");
      
      setEmployees(employees.map(emp => emp.id === editData.id ? response.data.user : emp));
      setIsEditModalOpen(false); 
    } catch (error) {
      toast.error("Failed to update employee.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <PageTitle title="Team & Employees" subtitle="Manage your HR team and system administrators." />
        <Link to="/employees/new" className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-2.5 rounded-lg font-semibold transition-colors">
          <Plus size={20} /> Add Employee
        </Link>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="Search employees..." className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-sm border-b border-gray-100">
                <th className="py-4 px-6 font-medium">Name</th>
                <th className="py-4 px-6 font-medium">Email</th>
                <th className="py-4 px-6 font-medium">Role</th>
                <th className="py-4 px-6 font-medium">Status</th>
                <th className="py-4 px-6 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="py-8 text-center text-gray-500">Loading team...</td></tr>
              ) : employees.length === 0 ? (
                <tr><td colSpan="5" className="py-8 text-center text-gray-500">No employees found.</td></tr>
              ) : (
                employees.map((emp) => (
                  <tr key={emp.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-800">{emp.name}</td>
                    <td className="py-4 px-6 text-gray-500">{emp.email}</td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium capitalize ${emp.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {emp.role === 'admin' && <Shield size={12} />}
                        {emp.role}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${emp.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {emp.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 flex justify-end gap-3">
                      <button onClick={() => openEditModal(emp)} className="text-gray-400 hover:text-blue-500 transition-colors" title="Edit">
                        <Edit size={18} />
                      </button>
                      <button onClick={() => handleDelete(emp.id)} className="text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 👇 Edit Employee Modal 👇 */}
      {isEditModalOpen && editData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-semibold text-lg text-gray-800">Edit Employee</h3>
              <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleUpdate} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input 
                  type="text" required
                  value={editData.name} 
                  onChange={(e) => setEditData({...editData, name: e.target.value})} 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-yellow-500" 
                />
              </div>
              
              {/* 👇 Yahan Text Input ko hata kar DYNAMIC SELECT laga diya hai 👇 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select 
                  required
                  value={editData.role} 
                  onChange={(e) => setEditData({...editData, role: e.target.value})} 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                >
                  <option value="" disabled>Select a Role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select 
                  value={editData.status} 
                  onChange={(e) => setEditData({...editData, status: e.target.value})} 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={updating} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-2 font-semibold rounded-lg transition-colors">
                  <Save size={16} /> {updating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}